import Form, { type IChangeEvent } from '@rjsf/core'
import type { Requisition } from 'prove-common'
import { customizeValidator } from '@rjsf/validator-ajv8'
import {
  CheckIcon,
  Mtds,
  SampleResultStatus,
  SampleResultTag,
  useErrorToast,
  useFinalizeRequisitionMutation
} from 'prove-common'
import { templates, widgets } from '../ui-schema-components'
import {
  FinalizeSampleFormData,
  getFinalizeSampleSchema
} from '../../schemas/finalize-sample-schema'
import { finalizeSampleUiSchema } from '../../schemas/finalize-sample-ui-schema'
import { useUpdateRequisitionFollowupMutation } from '../../hooks/useRequisition'

type FinalizeSampleProps = {
  requisition: Requisition
  onFinalizedSuccess?: () => void
  onFinalizedError?: (error: Error) => void
  onCanceled: () => void
  isEdit: boolean | undefined
}

export const FinalizeSample = ({
  requisition,
  onFinalizedSuccess,
  onFinalizedError,
  onCanceled,
  isEdit
}: FinalizeSampleProps) => {
  const finalizeRequisition = useFinalizeRequisitionMutation()
  const updateRequisitionFollowupMutation =
    useUpdateRequisitionFollowupMutation()
  const showError = useErrorToast(
    isEdit ? 'lagre endringene på prøven' : 'ferdigstille prøven'
  )

  const validator = customizeValidator<FinalizeSampleFormData>()
  const onSubmit = ({
    formData
  }: IChangeEvent<FinalizeSampleFormData>): void => {
    if (!formData) return
    const follwoUpComment =
      formData.followUpComment === '' ? undefined : formData.followUpComment
    const mutation = isEdit
      ? updateRequisitionFollowupMutation.mutateAsync({
          requisitionId: requisition.id,
          etag: requisition.etag,
          body: {
            followUp: formData.followUp,
            followUpComment: follwoUpComment
          }
        })
      : finalizeRequisition.mutateAsync({
          requisitionId: requisition.id,
          etag: requisition.etag,
          body: {
            confirmedBy: 'self',
            followUp: formData.followUp,
            followUpComment: follwoUpComment
          }
        })

    mutation
      .then(() => onFinalizedSuccess?.())
      .catch((error) => {
        showError(error)
        onFinalizedError?.(error)
      })
  }
  return (
    <Mtds.Grid data-gap='5'>
      {requisition.results?.status === 'POSITIVE' && (
        <Mtds.Card data-color='danger' className='bg-danger-surface-tinted'>
          <Mtds.Flex data-align='center'>
            Prøven har minst en påvist analyse{' '}
            <SampleResultTag status={SampleResultStatus.positive} />
          </Mtds.Flex>
        </Mtds.Card>
      )}
      <Form<FinalizeSampleFormData>
        noHtml5Validate
        id='finalize-sample-form'
        schema={
          isEdit
            ? getFinalizeSampleSchema(
                requisition.followUp,
                requisition.followUpComment
              )
            : getFinalizeSampleSchema()
        }
        uiSchema={finalizeSampleUiSchema}
        widgets={widgets}
        templates={templates}
        showErrorList={false}
        validator={validator}
        onSubmit={onSubmit}
        experimental_defaultFormStateBehavior={{
          constAsDefaults: 'skipOneOf'
        }}
      >
        <></>
      </Form>
      <Mtds.Flex data-justify='space-between'>
        <Mtds.Button data-variant={'secondary'} onClick={onCanceled}>
          Avbryt
        </Mtds.Button>
        <Mtds.Button
          data-variant='primary'
          type='submit'
          form='finalize-sample-form'
          aria-busy={
            finalizeRequisition.isPending ||
            updateRequisitionFollowupMutation.isPending
          }
          disabled={
            finalizeRequisition.isPending ||
            updateRequisitionFollowupMutation.isPending
          }
        >
          <CheckIcon />
          {isEdit ? 'Lagre' : 'Ferdigstill og avskriv'}
        </Mtds.Button>
      </Mtds.Flex>
    </Mtds.Grid>
  )
}
